import { httpsCallable } from 'firebase/functions';
import type { FuelCategory, FuelPricingSettings, FuelType } from '@kawai/shared';
import { functions } from '../firebase.js';

/**
 * F-03 / F-16: 燃料油価格 Callable のクライアント側ラッパ。
 *
 * - `fuelSetPrices`: 基準価格 (haioku / regular / keiyu / touyu) を一括更新
 * - `fuelSetPriceList`: 一覧表 (設定された区分 × 4 燃料) を一括更新
 * - `fuelSetSettings`: 設定（一覧表の区分 / 派生価格の値引き幅）を一括更新
 *
 * Functions 側で zod 検証 / claims チェック / Firestore TX (本体 doc +
 * fuel_price_history + audit_logs) を実施するため、ここではただ呼び出すだけ。
 */

export type SetFuelPricesPayload = Record<FuelType, number>;

/** F-16: 区分 id → 4 燃料。区分 id 集合は fuel_settings と一致している必要がある。 */
export type SetFuelPriceListPayload = {
  rows: Record<string, Record<FuelType, number>>;
};

export type SetFuelSettingsPayload = {
  categories: FuelCategory[];
  pricing: FuelPricingSettings;
};

interface OkResponse {
  ok: true;
}

const setPricesCallable = httpsCallable<SetFuelPricesPayload, OkResponse>(
  functions,
  'fuelSetPrices',
);

const setPriceListCallable = httpsCallable<
  SetFuelPriceListPayload,
  OkResponse
>(functions, 'fuelSetPriceList');

const setSettingsCallable = httpsCallable<SetFuelSettingsPayload, OkResponse>(
  functions,
  'fuelSetSettings',
);

/** 基準価格を更新する。Functions 側で 50〜300 整数の zod 検証あり。 */
export async function callFuelSetPrices(
  payload: SetFuelPricesPayload,
): Promise<OkResponse> {
  const res = await setPricesCallable(payload);
  return res.data;
}

/**
 * 一覧表を更新する。Functions 側で 50〜500 整数 × 全区分の zod 検証に加え、
 * 区分 id 集合が fuel_settings と一致することを検証する（不一致は failed-precondition）。
 */
export async function callFuelSetPriceList(
  payload: SetFuelPriceListPayload,
): Promise<OkResponse> {
  const res = await setPriceListCallable(payload);
  return res.data;
}

/** F-16: 設定（区分 / 値引き幅）を更新する。tenant-admin 以上のみ。 */
export async function callFuelSetSettings(
  payload: SetFuelSettingsPayload,
): Promise<OkResponse> {
  const res = await setSettingsCallable(payload);
  return res.data;
}
